import { Avatar as ChakraAvatar, forwardRef } from '@chakra-ui/react';
import type { AvatarProps as ChakraAvatarProps } from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';

export interface AvatarProps
  extends Omit<ChakraAvatarProps, 'colorScheme' | 'showBorder' | 'name'> {
  /**
   * The color mode that `Avatar` will display.
   * @default light
   */
  colorMode?: ColorMode;

  /**
   * The size of the Avatar.
   * @default md
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * The name of the person or entity in the avatar. If src has
   * loaded, the name will be used as the alt attribute of the img.
   * If src is not loaded, the name will be used to create the initials
   */
  name: ChakraAvatarProps['name'];

  /**
   * The color of the circle, behind the initials text.
   */
  backgroundColor?: string;

  /**
   * The color of the initials text
   */
  foregroundColor?: string;

  /**
   * The color of the avatar border
   */
  borderColor?: string;

  /**
   * The image url of the Avatar
   */
  src?: string;

  /**
   * A function to generate the one or two letter string that is
   * is displayed as the avatar fallback. This optional function
   * will override the built in logic.
   */
  getInitials?: ChakraAvatarProps['getInitials'];
}

/**
 * The `Avatar` component is used to represent user, and displays the profile picture, initials or fallback icon.
 */
export const Avatar = forwardRef<AvatarProps, 'span' | 'div'>(
  (
    {
      foregroundColor,
      colorMode: colorModeProp,
      name,
      size = 'md',
      ...rest
    }: AvatarProps,
    ref
  ) => {
    const { colorMode } = useColorMode(colorModeProp);

    return (
      <ChakraAvatar
        ref={ref}
        colorScheme={colorMode}
        showBorder={true}
        color={foregroundColor}
        size={size}
        name={name?.trim() || undefined}
        {...rest}
      />
    );
  }
);
